import { CameraSource } from "@capacitor/camera";
import styled from "@emotion/styled";
import { useMutation } from "@tanstack/react-query";
import React from "react";
import { match } from "ts-pattern";
import { API } from "../../../API";
import { getDevicePhotoForDocument } from "../../../get_device_photo";
import useOpen from "../../../hooks/useOpen";
import {
  AgencyId,
  CaregiverComplianceItemMobile,
  CaregiverDocumentTypeGroupInfo,
  CaregiverDocumentTypeId,
} from "../../../messages";
import { STRINGS } from "../../../strings";
import { translate } from "../../../translate";
import { assertDefined } from "../../../utils/assertUndefinedable";
import { HashMap } from "../../../utils/HashMap";
import { CHECK_ICON, DOCUMENT_OUTLINE_ICON, UPLOAD_ICON_2 } from "../../../utils/svgs_new";
import { AppBottomSheet } from "../../Generic/AppBottomSheet";
import { AppButton } from "../../Generic/AppButton";
import { AppIcon } from "../../Generic/AppIcon";
import { If } from "../../Generic/If";
import { isDefined } from "../../Generic/IsDefined";
import RadioGroup from "../../Generic/RadioGroup";
import { openScanner } from "../../MedflytScanner";
import ComplianceUploadDocumentModal from "./ComplianceUploadDocumentModal";
import { UploadDocumentParams } from "./hooks/useUploadDocument";

interface Props {
  api: API;
  agencyId: AgencyId;
  isOpen: boolean;
  item: CaregiverComplianceItemMobile;
  onRequestClose: () => void;
  onError: (params: { title: string; message: string }) => void;
  onRequestRefresh: () => void;
  onRequestNavigateToComplianceDocumentForm: (documentTypeId: CaregiverDocumentTypeId) => void;
}

interface GroupParent {
  isGroup: boolean;
  isSelected: boolean;
  base64: string | null;
}

export type SelectionMap = HashMap<CaregiverDocumentTypeId, GroupParent>;

type UploadDocument = UploadDocumentParams & { base64: string };

const UploadGroupDocumentModal = (props: Props) => {
  const [selectionMap, setSelectionMap] = React.useState<SelectionMap>(() =>
    getInitialSelectionMap(props.item)
  );
  const { childToParentsMap, idToDocMap } = getChildToParentMap(
    { ...props.item.documentType, parentId: null },
    new Map(),
    new Map()
  );
  const [uploadDocument, setUploadDocument] = React.useState<UploadDocumentParams | null>(null);
  const uploadDocumentSelectionModal = useOpen();
  const selectedFormDocument = getFormDocumentChildOfSelectedRadioParent(selectionMap, idToDocMap);
  const selectedUploadedDocuments = [...selectionMap.entries()].flatMap(([key, value]) => {
    if (!value.isSelected || value.base64 === null || value.isGroup) {
      return [];
    }

    return [{ id: key, base64: assertDefined(value.base64) }];
  });
  const areAllChildrenFormDocuments =
    props.item.documentType.children.length > 0 &&
    !props.item.documentType.children.some((child) => !child.isFillable);

  const mutateUploadDocument = useMutation(
    (upload: UploadDocument) => {
      return upload.isPassport
        ? props.api.uploadMedflytPassportDocument({
            base64: upload.base64,
            categoryId: CaregiverDocumentTypeId.unwrap(upload.id),
            documentTypeId: upload.id,
            documentName: upload.name,
            categoryName: upload.name,
            expiryDate: null,
          })
        : props.api.uploadMedflytPassportAgencyDocument(props.agencyId, upload.id, {
            base64: upload.base64,
            expiryDate: null,
          });
    },
    {
      onError: () => {
        props.onError({
          title: translate(STRINGS.ERROR),
          message: translate(STRINGS.SOMETHING_WENT_WRONG),
        });
      },
      onSuccess: () => handleUploaded(),
    }
  );

  const handleOpenCamera = () => {
    uploadDocumentSelectionModal.actions.dismiss();
    openScanner(
      handleFileSelected,
      (message) => props.onError({ title: translate(STRINGS.ERROR), message: message }),
      {}
    );
  };

  const handleOpenGallery = () => {
    uploadDocumentSelectionModal.actions.dismiss();
    getDevicePhotoForDocument({
      width: 1080,
      height: 1080,
      source: CameraSource.Photos,
    })
      .then((base64) => {
        if (base64 !== null) {
          handleFileSelected(base64);
        }
      })
      .catch((e) => console.error(e));
  };

  const handleChooseDocumentTypeToUpload = (document: CaregiverDocumentTypeGroupInfo) => {
    setUploadDocument({
      id: document.id,
      name: document.name,
      isPassport: document.isPassport,
    });
  };

  const handleSelectChild = (child: CaregiverDocumentTypeGroupInfo) => {
    let newMap = selectionMap;

    if (child.parentId !== null) {
      const parent = idToDocMap.get(child.parentId);

      if (parent !== undefined && parent.groupType === "or") {
        parent.children.forEach((c) => {
          newMap = newMap.delete(c.id);
        });
      }

      newMap = newMap.set(child.parentId, {
        isGroup: true,
        isSelected: true,
        base64: null,
      });
    }

    newMap = newMap.set(child.id, {
      isGroup: child.groupType !== null,
      isSelected: true,
      base64: null,
    });

    setSelectionMap(newMap);

    if (child.groupType === null) {
      handleChooseDocumentTypeToUpload(child);
    }
  };

  const handleFileSelected = (base64: string) => {
    if (uploadDocument === null) {
      return;
    }

    setSelectionMap(
      selectionMap.set(uploadDocument.id, {
        isGroup: false,
        isSelected: true,
        base64: base64,
      })
    );
  };

  const handleSelectParent = (parent: CaregiverDocumentTypeGroupInfo) => {
    const old = [...selectionMap.entries()].map(
      ([key, value]): [CaregiverDocumentTypeId, GroupParent] => [
        key,
        {
          ...value,
          isSelected:
            key === parent.id || (childToParentsMap.get(key)?.includes(parent.id) ?? false),
        },
      ]
    );

    setSelectionMap(
      HashMap.fromArray([...old, [parent.id, { isGroup: true, isSelected: true, base64: null }]])
    );
  };

  const handleUploaded = () => {
    props.onRequestRefresh();
    handleRequestClose();
  };

  const handleRequestClose = () => {
    setSelectionMap(getInitialSelectionMap(props.item));
    setUploadDocument(null);
    props.onRequestClose();
  };

  const handleUnSelectChild = (childId: CaregiverDocumentTypeId) => {
    setSelectionMap(selectionMap.delete(childId));
    handleSelectChild({ ...props.item.documentType, parentId: null });
  };

  const handleNavigateToFormDocument = (documentTypeId: CaregiverDocumentTypeId) => {
    handleRequestClose();
    props.onRequestNavigateToComplianceDocumentForm(documentTypeId);
  };

  const handleClickUploadDocument = (document: CaregiverDocumentTypeGroupInfo) => {
    if (document.isFillable) {
      handleNavigateToFormDocument(document.id);
      return;
    }

    uploadDocumentSelectionModal.actions.open();
  };

  const handleUploadDocuments = () => {
    if (selectedFormDocument !== null) {
      handleNavigateToFormDocument(selectedFormDocument.id);
      return;
    }

    if (selectedUploadedDocuments.length === 0) {
      return;
    }

    selectedUploadedDocuments
      .map((selectedDocument) => {
        const document = assertDefined(idToDocMap.get(selectedDocument.id));

        return {
          ...document,
          base64: selectedDocument.base64,
        };
      })
      .forEach((upload) => mutateUploadDocument.mutate(upload));
  };

  return (
    <>
      <AppBottomSheet
        isOpen={props.isOpen}
        rounded={true}
        title={props.item.documentType.name}
        onRequestClose={handleRequestClose}
        restrictHeight={false}
      >
        <UploadGroupItemDocumentSection
          document={{ ...props.item.documentType, parentId: null }}
          isRoot={true}
          isSelected={false}
          hasFile={false}
          isUploadButtonDisabled={false}
          selectionMap={selectionMap}
          onSelectParent={handleSelectParent}
          onSelectChild={handleSelectChild}
          onUnSelectChild={handleUnSelectChild}
          onClickUploadDocument={handleClickUploadDocument}
        />
        <If condition={!areAllChildrenFormDocuments}>
          <UploadGroupDocumentModal.ButtonsWrapper>
            <AppButton variant="outline" color="primary" onClick={props.onRequestClose}>
              {translate(STRINGS.CANCEL)}
            </AppButton>
            <AppButton
              variant="filled"
              color="primary"
              isLoading={mutateUploadDocument.isLoading}
              disabled={selectedFormDocument === null && selectedUploadedDocuments.length === 0}
              onClick={handleUploadDocuments}
            >
              {translate(STRINGS.ADD)}
            </AppButton>
          </UploadGroupDocumentModal.ButtonsWrapper>
        </If>
      </AppBottomSheet>
      <ComplianceUploadDocumentModal
        isOpen={uploadDocumentSelectionModal.isOpen}
        onClickOpenCamera={handleOpenCamera}
        onClickOpenGallery={handleOpenGallery}
        onRequestClose={uploadDocumentSelectionModal.actions.dismiss}
      />
    </>
  );
};

function UploadGroupItemDocumentSection(props: {
  document: CaregiverDocumentTypeGroupInfo;
  isRoot: boolean;
  selectionMap: SelectionMap;
  isSelected: boolean;
  hasFile: boolean;
  isUploadButtonDisabled: boolean;
  onSelectParent: (parent: CaregiverDocumentTypeGroupInfo) => void;
  onSelectChild: (child: CaregiverDocumentTypeGroupInfo) => void;
  onUnSelectChild: (childId: CaregiverDocumentTypeId) => void;
  onClickUploadDocument: (document: CaregiverDocumentTypeGroupInfo) => void;
}) {
  const isSelected =
    props.isSelected || (props.selectionMap.get(props.document.id)?.isSelected ?? false);
  const hasFile = props.hasFile || isDefined(props.selectionMap.get(props.document.id)?.base64);
  const disabled =
    props.isUploadButtonDisabled ||
    (props.selectionMap.get(props.document.id)?.isSelected !== true && !props.isRoot) ||
    ![...props.selectionMap.values()].some((value) => !value.isGroup);

  return match(props.document.groupType)
    .with(null, () => (
      <UploadDocumentButton
        hasFile={hasFile}
        disabled={disabled}
        document={props.document}
        onClick={props.onClickUploadDocument}
      />
    ))
    .with("and", () => (
      <UploadGroupItemDocumentSectionAnd
        {...props}
        isSelected={isSelected}
        hasFile={hasFile}
        isUploadButtonDisabled={disabled}
        onSelectParent={props.onSelectParent}
        onSelectChild={props.onSelectChild}
      />
    ))
    .with("or", () => (
      <UploadGroupItemDocumentSectionOr
        {...props}
        isSelected={isSelected}
        hasFile={hasFile}
        isUploadButtonDisabled={disabled}
        onSelectParent={props.onSelectParent}
        onSelectChild={props.onSelectChild}
        onUnSelectChild={props.onUnSelectChild}
      />
    ))
    .exhaustive();
}

function UploadGroupItemDocumentSectionAnd(props: {
  document: CaregiverDocumentTypeGroupInfo;
  selectionMap: SelectionMap;
  isSelected: boolean;
  hasFile: boolean;
  isUploadButtonDisabled: boolean;
  onSelectParent: (parent: CaregiverDocumentTypeGroupInfo) => void;
  onSelectChild: (child: CaregiverDocumentTypeGroupInfo) => void;
  onUnSelectChild: (childId: CaregiverDocumentTypeId) => void;
  onClickUploadDocument: (document: CaregiverDocumentTypeGroupInfo) => void;
}) {
  return (
    <>
      {props.document.children.map((child) => {
        const childEelement = (
          <UploadGroupItemDocumentSection
            key={child.id.toString()}
            document={child}
            isRoot={false}
            isSelected={props.isSelected}
            hasFile={props.hasFile}
            isUploadButtonDisabled={props.isUploadButtonDisabled}
            selectionMap={props.selectionMap}
            onSelectParent={props.onSelectParent}
            onSelectChild={props.onSelectChild}
            onUnSelectChild={props.onUnSelectChild}
            onClickUploadDocument={props.onClickUploadDocument}
          />
        );

        return child.children.length === 0 ? (
          <DocumentGroupWrapper key={`group-${child.id}`} isSelected={props.isSelected}>
            <SmallText>{child.name}</SmallText>
            {childEelement}
          </DocumentGroupWrapper>
        ) : (
          childEelement
        );
      })}
    </>
  );
}

function UploadGroupItemDocumentSectionOr(props: {
  document: CaregiverDocumentTypeGroupInfo;
  isRoot: boolean;
  selectionMap: SelectionMap;
  isSelected: boolean;
  hasFile: boolean;
  isUploadButtonDisabled: boolean;
  onSelectParent: (parent: CaregiverDocumentTypeGroupInfo) => void;
  onSelectChild: (child: CaregiverDocumentTypeGroupInfo) => void;
  onUnSelectChild: (childId: CaregiverDocumentTypeId) => void;
  onClickUploadDocument: (document: CaregiverDocumentTypeGroupInfo) => void;
}) {
  const handleSelect = (event: React.ChangeEvent<HTMLSelectElement>) => {
    const document = props.document.children.find(
      (child) => child.id.toString() === event.target.value
    );

    if (document === undefined) {
      const childDocumentId = [...props.selectionMap.keys()].find((id) =>
        props.document.children.some((child) => child.id === id)
      );

      if (childDocumentId !== undefined) {
        props.onUnSelectChild(childDocumentId);
      }

      return;
    }

    props.onSelectChild(document);
  };

  if (props.isRoot) {
    const handleSelectParent = (value: string) => {
      const document = props.document.children.find((child) => child.id.toString() === value);

      if (document === undefined) {
        return;
      }

      const onSelect = document.groupType === null ? props.onSelectChild : props.onSelectParent;

      onSelect(document);
    };

    return (
      <RadioGroup.Root onValueChange={handleSelectParent}>
        {props.document.children.map((child) => (
          <RadioSection key={child.id.toString()}>
            <RadioWrapper>
              <RadioGroup.Item id={child.id.toString()} value={child.id.toString()} />
              <Label htmlFor={child.id.toString()}>{child.name}</Label>
            </RadioWrapper>
            <UploadGroupItemDocumentSection
              key={child.id.toString()}
              document={child}
              isRoot={false}
              isSelected={props.isSelected}
              hasFile={props.hasFile}
              isUploadButtonDisabled={props.isUploadButtonDisabled}
              selectionMap={props.selectionMap}
              onSelectParent={props.onSelectParent}
              onSelectChild={props.onSelectChild}
              onUnSelectChild={props.onUnSelectChild}
              onClickUploadDocument={props.onClickUploadDocument}
            />
          </RadioSection>
        ))}
      </RadioGroup.Root>
    );
  }

  return (
    <DocumentGroupWrapper isSelected={props.isSelected}>
      <SmallText>{props.document.name}</SmallText>
      <Select onChange={handleSelect} required={true} disabled={!props.isSelected}>
        <option value="">{translate(STRINGS.SELECT)}</option>
        {props.document.children.map((child) => (
          <option key={child.id.toString()} value={child.id.toString()}>
            {child.name}
          </option>
        ))}
      </Select>
      <UploadDocumentButton
        hasFile={props.hasFile}
        disabled={props.isUploadButtonDisabled}
        document={props.document}
        onClick={props.onClickUploadDocument}
      />
    </DocumentGroupWrapper>
  );
}

function UploadDocumentButton(props: {
  hasFile: boolean;
  disabled: boolean;
  document: CaregiverDocumentTypeGroupInfo;
  onClick: (document: CaregiverDocumentTypeGroupInfo) => void;
}) {
  const buttonText = (() => {
    if (props.hasFile) {
      return translate(STRINGS.DOCUMENT_SELECTED);
    }

    if (props.document.isFillable) {
      return translate(STRINGS.SUBMIT_FORM);
    }

    return translate(STRINGS.SELECT_DOCUMENT);
  })();

  const buttonIcon = (() => {
    if (props.hasFile) {
      return CHECK_ICON;
    }

    if (props.document.isFillable) {
      return DOCUMENT_OUTLINE_ICON;
    }

    return UPLOAD_ICON_2;
  })();

  const handleClick = () => {
    if (props.disabled) {
      return;
    }

    props.onClick(props.document);
  };

  return (
    <UploadDocumentButton.Styled
      isSuccess={props.hasFile}
      disabled={props.disabled}
      onClick={handleClick}
    >
      <AppIcon icon={buttonIcon} />
      {buttonText}
    </UploadDocumentButton.Styled>
  );
}

function getFormDocumentChildOfSelectedRadioParent(
  selectionMap: SelectionMap,
  idToDocMap: Map<CaregiverDocumentTypeId, CaregiverDocumentTypeGroupInfo>
) {
  for (const [id, value] of [...selectionMap.entries()]) {
    if (!value.isSelected) {
      continue;
    }

    const selectedItem = idToDocMap.get(id);

    if (selectedItem === undefined) {
      continue;
    }

    if (selectedItem.isFillable) {
      return selectedItem;
    }
  }

  return null;
}

function getInitialSelectionMap(item: CaregiverComplianceItemMobile): SelectionMap {
  if (
    item.documentType.groupType === "and" &&
    !item.documentType.children.some((child) => child.children.length > 0)
  ) {
    return HashMap.fromArray([
      [item.documentType.id, { isGroup: true, isSelected: true, base64: null }],
      ...item.documentType.children.map((child): [CaregiverDocumentTypeId, GroupParent] => [
        child.id,
        { isGroup: false, isSelected: true, base64: null },
      ]),
    ]);
  }

  return HashMap.empty();
}

UploadGroupDocumentModal.ButtonsWrapper = styled.div`
  display: flex;
  flex-direction: row;
  gap: 8px;
  button {
    width: 100%;
  }
`;

UploadDocumentButton.Styled = styled.button<{ isSuccess: boolean }>`
  width: 100%;
  padding: 12px 0;
  background: ${(props) => (props.isSuccess ? "var(--green-soft)" : "#ebf2fe")};
  color: ${(props) => (props.isSuccess ? "var(--teal)" : "var(--primary-color)")};
  border: none;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 12px;
  font-weight: 600;
  border-radius: 12px;
  margin: 4px 0;

  &:disabled {
    opacity: 0.4;
  }
`;

const RadioSection = styled.div`
  padding-bottom: 22px;
`;

const Select = styled.select`
  margin: 4px 0;
  padding: 12px 16px;
  border: 1px solid var(--gray-200);
  border-radius: 12px;
  width: 100%;
  color: var(--gray-700);

  &:invalid {
    color: var(--gray-400);
    border-color: var(--gray-100);
  }
`;

const RadioWrapper = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: 12px;
  margin-bottom: 8px;
`;

const Label = styled.label`
  color: var(--gray-600);
  font-size: 14px;
  font-weight: 500;
`;

const SmallText = styled.small`
  color: var(--gray-600);
  font-weight: 400;
  font-size: 12px;
`;

const DocumentGroupWrapper = styled.div<{ isSelected: boolean }>`
  margin-bottom: 8px;
  opacity: ${({ isSelected }) => (isSelected ? 1 : 0.4)};
`;

function getChildToParentMap(
  documentType: CaregiverDocumentTypeGroupInfo,
  childToParentsMap: Map<CaregiverDocumentTypeId, CaregiverDocumentTypeId[]>,
  idToDocMap: Map<CaregiverDocumentTypeId, CaregiverDocumentTypeGroupInfo>,
  parentIds: CaregiverDocumentTypeId[] = []
) {
  idToDocMap.set(documentType.id, documentType);

  const parents = [
    ...(childToParentsMap.get(documentType.id) ?? []),
    ...parentIds,
    ...(documentType.parentId !== null ? [documentType.parentId] : []),
  ];

  childToParentsMap.set(documentType.id, parents);

  for (const child of documentType.children) {
    getChildToParentMap(child, childToParentsMap, idToDocMap, parents);
  }

  return { childToParentsMap, idToDocMap };
}

export default UploadGroupDocumentModal;
